import type { LevelParts } from "./levelParts";

/**
 * Brings the views and the panels up to date after the sim moved (ticks or a command): the road views, the placement
 * marks for a new offer or pick, the HUD, the tray and the tower sheet, the framing insets, and the placement camera.
 */
export class LevelSync {
  private lastOfferId = 0;
  private lastHadPick = false;

  constructor(private readonly p: LevelParts) {}

  /**
   * `ticked` is the frame loop's call, after ticks whose view hooks (towers.onTick, troopViews.onTick) already ran
   * once per tick, so they are not run a second time here. `undoable`: UNDO is offered now. `debugDriving`: a headless
   * capture's command, which aims the camera itself.
   */
  apply(force: boolean, ticked: boolean, undoable: boolean, debugDriving: boolean): void {
    const p = this.p, sim = p.sim, scene = p.scene;
    p.rally.settle();
    scene.noteRoad(force);
    scene.ground.setTowers(sim.towerSlots);                // flagstone pads + contact shadows; no-op unless towers changed
    if (!ticked) { scene.towers.onTick(); scene.troopViews.onTick(); }
    const offer = sim.currentOffer;
    const offerId = offer?.id ?? 0;
    const hasPick = offer !== null && offer.hasPick;
    let reaim = false;
    if (force || offerId !== this.lastOfferId || hasPick !== this.lastHadPick) {
      reaim = true;
      this.lastOfferId = offerId;
      this.lastHadPick = hasPick;
      p.taps.showOffer();
      if (p.rally.armed) scene.placement.showRallyCells(p.rally.cells());   // a forced refresh cleared the marks
    }
    p.hud.refresh(sim, p.rewards.shown(sim.points));
    p.hud.bossBar.heal(sim.enemySlots.some((e) => e.alive && e.isBoss));   // M30 #3: never outstays its boss
    p.taps.refreshHand(undoable);
    p.hud.setUndoable(undoable);
    const info = p.towerInfo;
    if (info.isOpen && (offer === null || !hasPick)) {
      info.refresh(sim);
      const t = sim.findTower(info.openTowerId);
      if (t) scene.placement.showRange(t.cell, sim.rangeOf(t).toNumber(), sim.balance.towers[t.archetype].minRange.toNumber());
    } else if (info.isOpen) info.close();
    if (info.isOpen) p.hand.root.classList.add("hidden");
    p.hud.setControlsSuppressed(p.ended);           // the tower sheet only lifts the row (LevelLayout.update)
    // The turn control shares the bottom-right with the card tray and the tower sheet, so it steps aside for them.
    // The thumb cluster steps aside for the tower sheet and while a card is being placed; over a dealt hand it rides
    // above the tray (LevelLayout.update).
    p.hud.rotate.classList.toggle("hidden", p.end.shown || info.isOpen || hasPick);
    p.layout.update();
    if (reaim && !p.ended && !debugDriving) p.camFollow.follow(sim, p.taps.ghostIndex, p.taps.focusCell());
    else if (debugDriving) p.camFollow.skipRoad(sim);
  }
}
